import {
  canonicalStringify,
  hashSyncPayloadForVersion,
  payloadVersionForSyncPayload,
  projectSyncPayload,
  sha256Hex,
  validateSyncPayloadForVersion
} from './canonical';
import {
  SYNC_MAX_BATCH_BYTES,
  SYNC_MAX_MUTATIONS_PER_BATCH,
  SYNC_PAYLOAD_VERSION_V1,
  SYNC_PROTOCOL_V1,
  SYNC_PROTOCOL_VERSION,
  canonicalizationForProtocol,
  type SyncBaseRevision,
  type SyncBatch,
  type SyncEntityType,
  type SyncMutation,
  type SyncMutationDraft,
  type SyncPayloadVersion,
  type SyncProtocolVersion
} from './types';

export interface SyncMutationDraftWithBase<E extends SyncEntityType = SyncEntityType> extends SyncMutationDraft<E> {
  baseRevision: SyncBaseRevision;
}

interface CreateSyncBatchInput {
  deviceId: string;
  mutations: SyncMutationDraftWithBase[];
  protocolVersion?: SyncProtocolVersion;
  payloadVersion?: SyncPayloadVersion;
  batchId?: string;
  createdAt?: string;
  supersedesBatchId?: string;
}

const normalizeUuidLike = (value: string): string => value.toLowerCase();

const deepFreeze = <T>(value: T): T => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const child of Object.values(value as object)) deepFreeze(child);
  }
  return value;
};

export const createSyncBatch = async ({
  deviceId,
  mutations: drafts,
  batchId = globalThis.crypto.randomUUID(),
  createdAt = new Date().toISOString(),
  supersedesBatchId,
  protocolVersion = SYNC_PROTOCOL_VERSION,
  payloadVersion = SYNC_PAYLOAD_VERSION_V1
}: CreateSyncBatchInput): Promise<SyncBatch> => {
  if (drafts.length === 0) throw new Error('Um batch de sincronização precisa conter ao menos uma mutation.');
  if (drafts.length > SYNC_MAX_MUTATIONS_PER_BATCH) {
    throw new Error(`A operação excede o limite seguro de ${SYNC_MAX_MUTATIONS_PER_BATCH} alterações por sincronização.`);
  }

  const seenEntities = new Set<string>();
  const mutations: SyncMutation[] = [];
  for (const [sequence, draft] of drafts.entries()) {
    const entityId = normalizeUuidLike(draft.entityId);
    const entityKey = `${draft.entityType}:${entityId}`;
    if (seenEntities.has(entityKey)) throw new Error(`O batch contém mais de uma mutation para ${entityKey}.`);
    seenEntities.add(entityKey);

    const payload = projectSyncPayload(draft.entityType, draft.payload);
    const inferredPayloadVersion = payloadVersionForSyncPayload(draft.entityType, payload);
    if (payloadVersion !== SYNC_PAYLOAD_VERSION_V1 && payloadVersion !== inferredPayloadVersion) {
      throw new Error(`A mutation ${entityKey} não corresponde ao payloadVersion solicitado.`);
    }
    const mutationPayloadVersion = payloadVersion === SYNC_PAYLOAD_VERSION_V1
      ? inferredPayloadVersion
      : payloadVersion;
    if (protocolVersion === SYNC_PROTOCOL_V1 && mutationPayloadVersion !== SYNC_PAYLOAD_VERSION_V1) {
      throw new Error('O Sync Protocol V1 não pode criar ou enviar payload EPUB. Ative o Protocol V2 antes de sincronizar este conteúdo.');
    }
    validateSyncPayloadForVersion(draft.entityType, payload, mutationPayloadVersion);
    mutations.push({
      sequence,
      entityType: draft.entityType,
      entityId,
      operation: draft.operation,
      baseRevision: draft.baseRevision,
      payloadVersion: mutationPayloadVersion,
      payload,
      payloadHash: await hashSyncPayloadForVersion(draft.entityType, payload, mutationPayloadVersion)
    });
  }

  const envelope = {
    protocolVersion,
    canonicalization: canonicalizationForProtocol(protocolVersion),
    batchId: normalizeUuidLike(batchId),
    deviceId: normalizeUuidLike(deviceId),
    createdAt: new Date(createdAt).toISOString(),
    mutations,
    ...(supersedesBatchId ? { supersedesBatchId: normalizeUuidLike(supersedesBatchId) } : {})
  };
  const batch: SyncBatch = {
    ...envelope,
    requestHash: await sha256Hex(canonicalStringify(envelope))
  };
  if (new TextEncoder().encode(JSON.stringify(batch)).byteLength > SYNC_MAX_BATCH_BYTES) {
    throw new Error('A operação excede o limite seguro de 8 MiB por sincronização.');
  }
  return deepFreeze(batch);
};
